import React, { useState, useEffect, useContext, useCallback } from 'react';
import moment from 'moment';
import axios from 'axios';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { IoMdNotificationsOutline } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { authContext } from 'contexts/AuthContext';
import Drawer from 'components/Drawer';
import EditOrderWrapper from 'pages/Orders/components/EditOrderWrapper';

const formatNotificationDate = (notification) => {
  const rawValue =
    notification?.createdAt ||
    notification?.createdAtMs ||
    notification?.created_at ||
    notification?.date ||
    notification?.createdDate ||
    '';

  if (!rawValue) return '';

  let asDate;
  if (typeof rawValue?.toDate === 'function') {
    asDate = rawValue.toDate();
  } else if (typeof rawValue === 'number') {
    asDate = new Date(rawValue > 1e12 ? rawValue : rawValue * 1000);
  } else {
    asDate = new Date(rawValue);
  }

  if (Number.isNaN(asDate.getTime())) return '';

  return moment(asDate).format('D MMM, YYYY HH:mm');
};

const AdminNotificationDropdown = () => {
  const navigate = useNavigate();
  const { auth } = useContext(authContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const fetchNotifications = useCallback(async () => {
    if (!auth?.data?.token) return;

    try {
      setLoading(true);
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

      const response = await axios.post(
        graphqlEndpoint,
        {
          operationName: 'notifications',
          query: `query notifications($filter: [FilterMap], $pageNo: Int, $pageSize: Int, $sortCol: String, $sortDirection: String) {
            notifications(filter: $filter, pageNo: $pageNo, pageSize: $pageSize, sortCol: $sortCol, sortDirection: $sortDirection) {
              notifications {
                id
                message
                createdAt
                image
                isSeen
                isOpened
                isAdminOpened
                typeName
                trader {
                  id
                  firstName
                  lastName
                  entityName
                  callingCode {
                    id
                    callingCode
                  }
                  phone
                }
              }
              totalPages
              totalCount
            }
          }`,
          variables: {
            filter: [
              {
                key: 'forAdminOnly',
                value: true,
                type: 'BOOLEAN',
                op: 'EQ',
              },
            ],
            pageNo: 1,
            pageSize: 10,
            sortCol: null,
            sortDirection: null,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.data.token}`,
          },
        }
      );

      const notificationsData = response?.data?.data?.notifications?.notifications || [];
      setNotifications(notificationsData);

      // Count unread notifications (where isAdminOpened is false)
      const unreadCountValue = notificationsData.filter(n => !n.isAdminOpened).length;
      setUnreadCount(unreadCountValue);
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const markNotificationAsOpened = async (notificationId) => {
    if (!auth?.data?.token) return;

    try {
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

      await axios.post(
        graphqlEndpoint,
        {
          query: `mutation { openAdminNotification(notificationId: ${notificationId}) }`,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.data.token}`,
          },
        }
      );

      // Update local state to mark notification as opened
      setNotifications((prevNotifications) => {
        return prevNotifications.map((notif) => {
          if (notif.id === notificationId) {
            return { ...notif, isAdminOpened: true };
          }
          return notif;
        });
      });

      // Update unread count
      setUnreadCount((prev) => {
        const newCount = prev - 1;
        return Math.max(0, newCount);
      });
    } catch (error) {
      console.error('Error marking notification as opened:', error);
    }
  };

  const isFreightNotification = (notification) => {
    if (!notification) return false;
    const message = (notification.message || '').toLowerCase();
    const typeName = (notification.typeName || '').toLowerCase();
    return (
      message.includes('freight') ||
      typeName.includes('freight') ||
      typeName === 'freight'
    );
  };

  const extractOrderId = (notification) => {
    if (!notification) return null;
    const message = notification.message || '';

    if (notification.orderId && !Number.isNaN(Number(notification.orderId))) {
      return Number(notification.orderId);
    }
    if (notification.order?.id && !Number.isNaN(Number(notification.order.id))) {
      return Number(notification.order.id);
    }
    if (notification.entityId && !Number.isNaN(Number(notification.entityId))) {
      return Number(notification.entityId);
    }
    if (notification.referenceId && !Number.isNaN(Number(notification.referenceId))) {
      return Number(notification.referenceId);
    }

    const odrDateMatch = message.match(/#ODR_[A-Za-z0-9]+_([0-9,]+)/i);
    if (odrDateMatch && odrDateMatch[1]) {
      const cleanedId = odrDateMatch[1].replace(/,/g, '');
      if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) return Number(cleanedId);
    }

    const odrMatch = message.match(/#ODR_([0-9,]+)/i);
    if (odrMatch && odrMatch[1]) {
      const cleanedId = odrMatch[1].replace(/,/g, '');
      if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) return Number(cleanedId);
    }

    const orderMatch = message.match(/\border\b[^\d]*?([0-9,]+)/i);
    if (orderMatch && orderMatch[1]) {
      const cleanedId = orderMatch[1].replace(/,/g, '');
      if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) return Number(cleanedId);
    }

    const hashMatch = message.match(/#([0-9,]+)/);
    if (hashMatch && hashMatch[1]) {
      const cleanedId = hashMatch[1].replace(/,/g, '');
      if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) return Number(cleanedId);
    }

    const forMatch = message.match(/\bfor\b[^\d]*?([0-9,]+)/i);
    if (forMatch && forMatch[1]) {
      const cleanedId = forMatch[1].replace(/,/g, '');
      if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) return Number(cleanedId);
    }

    const allNumbers = message.match(/\b\d{3,7}\b/g);
    if (allNumbers && allNumbers.length) {
      const lastNum = Number(allNumbers[allNumbers.length - 1]);
      if (!Number.isNaN(lastNum) && lastNum > 0) return lastNum;
    }

    return null;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isAdminOpened) {
      markNotificationAsOpened(notification.id);
    }

    const message = (notification.message || '').toLowerCase();
    const orderId = extractOrderId(notification);

    // "submitted a purchase request" -> go to Approval tab
    if (message.includes('submitted a purchase request')) {
      setDropdownOpen(false);
      if (orderId) {
        navigate(`/orders/${orderId}?tab=Approval`, {
          state: { tab: 'Approval' },
        });
      } else {
        navigate('/orders');
      }
      return;
    }

    // "freight charge accepted" with "accept or reject" -> go to Approval tab
    if (message.includes('freight charge accepted') && message.includes('accept or reject')) {
      setDropdownOpen(false);
      if (orderId) {
        navigate(`/orders/${orderId}?tab=Approval`, {
          state: { tab: 'Approval' },
        });
      } else {
        navigate('/orders');
      }
      return;
    }

    // Freight notifications (like "initiate freight arrangement") -> open Edit Drawer
    const isFreight = isFreightNotification(notification);
    if (isFreight) {
      setDropdownOpen(false);
      if (orderId) {
        setSelectedOrderId(orderId);
        setIsEditDrawerOpen(true);
      }
    }
  };

  useEffect(() => {
    if (auth?.data?.token) {
      fetchNotifications();
      const intervalId = setInterval(() => {
        fetchNotifications();
      }, 30000); // poll every 30 seconds
      return () => clearInterval(intervalId);
    }
  }, [auth?.data?.token, fetchNotifications]);

  return (
    <>
      <Dropdown
        isOpen={dropdownOpen}
        toggle={toggle}
        direction="down"
        className="nav-item d-flex align-items-center"
        style={{ marginRight: '-8px' }}
      >
        <DropdownToggle
          tag="div"
          className="nav-link cursor-pointer d-flex align-items-center"
          style={{ padding: '4px' }}
        >
          <div className="position-relative d-inline-block">
            <IoMdNotificationsOutline size={26} color="#666" />
            {unreadCount > 0 && (
              <span
                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{
                  fontSize: '10px',
                  padding: '2px 5px',
                  minWidth: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        </DropdownToggle>

        <DropdownMenu
          container="body"
          className="font-manrope"
          style={{
            width: '380px',
            maxHeight: '500px',
            overflowY: 'auto',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }}
        >
          <div className="p-3 fw-bold border-bottom f-16" style={{ background: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
            Notifications
            {unreadCount > 0 && (
              <span className="ms-2 text-muted f-14 fw-normal">
                ({unreadCount} new)
              </span>
            )}
          </div>
          {loading && (
            <div className="p-4 text-center text-muted f-14">Loading...</div>
          )}
          {!loading && notifications.length === 0 && (
            <div className="p-4 text-center text-muted f-14">No notifications</div>
          )}
          {!loading && notifications.length > 0 && (
            <>
              {notifications.map((notif) => (
                <DropdownItem
                  key={notif.id}
                  className="text-wrap border-bottom p-3"
                  style={{
                    whiteSpace: 'normal',
                    cursor: 'pointer',
                    backgroundColor: notif.isAdminOpened ? '#ffffff' : '#e7f3ff',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    const newBgColor = notif.isAdminOpened ? '#f8f9fa' : '#d4ebff';
                    e.currentTarget.style.backgroundColor = newBgColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notif.isAdminOpened ? '#ffffff' : '#e7f3ff';
                  }}
                  toggle={false}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                    <div className="d-flex align-items-center gap-2">
                      {!notif.isAdminOpened && (
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#0a66c2',
                            flexShrink: 0,
                            marginTop: '4px'
                          }}
                        />
                      )}
                      <div className="text-secondary f-12 text-nowrap">
                        {formatNotificationDate(notif)}
                      </div>
                    </div>
                  </div>
                  <div
                    className="mb-0 f-13"
                    style={{
                      lineHeight: '1.5',
                      color: notif.isAdminOpened ? '#666' : '#000',
                      fontWeight: notif.isAdminOpened ? 'normal' : '500'
                    }}
                  >
                    {notif.message}
                  </div>
                  {notif.trader && (
                    <div className="mt-2 f-12 text-muted">
                      {notif.trader.firstName} {notif.trader.lastName}
                      {notif.trader.entityName && ` • ${notif.trader.entityName}`}
                      {notif.trader.phone && (
                        <>
                          {' • '}
                          {notif.trader.callingCode?.callingCode} {notif.trader.phone}
                        </>
                      )}
                    </div>
                  )}
                </DropdownItem>
              ))}
            </>
          )}
        </DropdownMenu>
      </Dropdown>

      <Drawer
        isOpen={isEditDrawerOpen}
        toggle={() => setIsEditDrawerOpen(false)}
        heading="Edit Order"
      >
        {selectedOrderId && (
          <EditOrderWrapper
            currentRow={{ id: selectedOrderId }}
            onCloseOrderPopup={() => setIsEditDrawerOpen(false)}
            onOrderUpdated={() => setIsEditDrawerOpen(false)}
          />
        )}
      </Drawer>
    </>
  );
};

export default AdminNotificationDropdown;